import React, { useEffect, useState } from "react";
import { Youtube, Trash2, Plus } from "lucide-react";
import { listYtTracks, addYtTrack, deleteYtTrack } from "../lib/api";

const SCENES = [
  { id: "pre",             label: "Pre-Event (before doors open)" },
  { id: "anticipation",    label: "Anticipation (\"Who's coming?\") — 22s" },
  { id: "sky",             label: "Sky of Stars (Anagha's Gallery)" },
  { id: "memories",        label: "Memories Slideshow" },
  { id: "cake_prep",       label: "Cake Cutting Buildup" },
  { id: "cake_celebrate",  label: "Cake Celebration Fireworks" },
  { id: "dinner",          label: "Dinner (Anagha's Messages)" },
  { id: "farewell",        label: "Final Farewell" },
];

export default function YoutubeMusicPanel() {
  const [tracks, setTracks] = useState([]);
  const [scene, setScene] = useState("sky");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const refresh = async () => {
    try { setTracks(await listYtTracks()); } catch { /* */ }
  };
  useEffect(() => { refresh(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true); setMsg("");
    try {
      await addYtTrack(scene, url.trim());
      setUrl("");
      setMsg("Added! Director will play it when this scene is active.");
      setTimeout(() => setMsg(""), 3500);
      await refresh();
    } catch (err) {
      setMsg(err?.response?.data?.detail || "Could not add — check the URL.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this YouTube track?")) return;
    try { await deleteYtTrack(id); await refresh(); } catch { /* */ }
  };

  const clearScene = async (sceneId, sceneLabel) => {
    const list = tracks.filter((t) => t.scene === sceneId);
    if (!list.length) return;
    if (!window.confirm(`Remove ALL ${list.length} track(s) from "${sceneLabel}"?`)) return;
    setBusy(true);
    try {
      await Promise.all(list.map((t) => deleteYtTrack(t.id)));
      await refresh();
    } catch { /* */ }
    setBusy(false);
  };

  const bySceneId = (sid) => tracks.filter((t) => t.scene === sid);

  return (
    <section className="mb-8 glass-deep rounded-3xl p-5 sm:p-7" data-testid="youtube-section">
      <div className="flex items-center gap-2 mb-1">
        <Youtube size={20} className="text-rose-600" />
        <h2 className="font-display text-xl text-ink">YouTube Background Music (per Director scene)</h2>
      </div>
      <p className="text-xs text-ink-soft mb-4 max-w-3xl">
        Paste any YouTube URL and pick which Director scene it should play on. The Director view
        will auto-play the songs invisibly in the background as that scene runs. Multiple songs
        per scene are allowed — they play in the order you add them and then repeat.
      </p>

      <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="yt-add-form">
        <select
          value={scene}
          onChange={(e) => setScene(e.target.value)}
          className="rounded-xl bg-white/70 border border-white/60 px-3 py-2 text-sm text-ink"
          data-testid="yt-scene-select"
        >
          {SCENES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          className="flex-1 rounded-xl bg-white/70 border border-white/60 px-3 py-2 text-sm text-ink"
          data-testid="yt-url-input"
        />
        <button
          type="submit"
          disabled={busy || !url.trim()}
          className="btn-royal text-sm py-2 px-4 disabled:opacity-60"
          data-testid="yt-add-btn"
        >
          <Plus size={14} /> {busy ? "Adding..." : "Add Track"}
        </button>
      </form>
      {msg && <p className="mb-3 text-xs text-plum" data-testid="yt-msg">{msg}</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {SCENES.map((s) => {
          const list = bySceneId(s.id);
          return (
            <div
              key={s.id}
              className="rounded-2xl bg-white/60 border border-white/60 p-3"
              data-testid={`yt-scene-${s.id}`}
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <p className="text-xs label-eyebrow text-plum truncate">{s.label}</p>
                {list.length > 0 && (
                  <button
                    onClick={() => clearScene(s.id, s.label)}
                    className="shrink-0 inline-flex items-center gap-1 text-[10px] text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-full px-2 py-0.5"
                    title={`Clear all ${list.length} tracks`}
                    data-testid={`yt-clear-scene-${s.id}`}
                  >
                    <Trash2 size={11} /> Clear all ({list.length})
                  </button>
                )}
              </div>
              {list.length === 0 ? (
                <p className="text-xs text-ink-soft italic">No tracks yet.</p>
              ) : (
                <ul className="space-y-1.5">
                  {list.map((t) => (
                    <li key={t.id} className="flex items-center gap-2 text-xs">
                      <img
                        src={`https://img.youtube.com/vi/${t.video_id}/default.jpg`}
                        alt=""
                        className="w-14 h-10 rounded object-cover"
                      />
                      <a
                        href={t.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 truncate text-ink hover:underline"
                        title={t.url}
                      >
                        {t.video_id}
                      </a>
                      <button
                        onClick={() => remove(t.id)}
                        className="text-rose-600 hover:text-rose-800 p-1"
                        title="Remove"
                        data-testid={`yt-delete-${t.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
